// Client-side image normalization before upload:
// resize to max 2500px width and re-encode as lossy WebP (q=0.9).
// Animated GIFs, WebPs, and unknown types are passed through untouched, but we
// still try to read dimensions so the server can store width/height for CLS /
// srcset hints on the frontend side.
//
// PNG / JPEG must go through the canvas: re-encoding is what strips EXIF
// (including GPS coordinates) before the bytes become public under /images/*.
// If that step fails we refuse the upload instead of silently sending the
// original bytes.

const MAX_WIDTH = 2500;
const QUALITY = 0.9;
const TRANSCODE_TYPES = new Set(["image/png", "image/jpeg"]);

// Encoders the re-encode may legitimately fall back to. Safari's canvas cannot
// encode WebP and returns a PNG instead of honoring the requested type.
const ENCODED_EXTENSION: Record<string, string> = {
  "image/webp": "webp",
  "image/png": "png",
};

export class ImageTranscodeError extends Error {
  constructor() {
    super(
      "画像を変換できませんでした（位置情報などのメタデータを除去できないため、アップロードを中止しました）",
    );
    this.name = "ImageTranscodeError";
  }
}

export type TranscodeResult = {
  file: File;
  width: number | null;
  height: number | null;
};

export async function transcodeForUpload(file: File): Promise<TranscodeResult> {
  if (!TRANSCODE_TYPES.has(file.type)) {
    // Passthrough (GIF for animation preservation, plus unknown MIMEs).
    // Try to read dimensions without re-encoding; fall back to null so the
    // upload still succeeds when the browser can't decode it.
    try {
      const bitmap = await createImageBitmap(file);
      const dims = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      return { file, ...dims };
    } catch {
      return { file, width: null, height: null };
    }
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new ImageTranscodeError();
  }

  try {
    const scale = bitmap.width > MAX_WIDTH ? MAX_WIDTH / bitmap.width : 1;
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new ImageTranscodeError();
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/webp", QUALITY),
    );
    if (!blob) throw new ImageTranscodeError();

    // Label the file with what the encoder actually produced, not what we
    // asked for, so the stored MIME / extension match the bytes.
    const extension = ENCODED_EXTENSION[blob.type];
    if (!extension) throw new ImageTranscodeError();

    const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
    const newFile = new File([blob], `${baseName}.${extension}`, {
      type: blob.type,
      lastModified: file.lastModified,
    });
    return { file: newFile, width: w, height: h };
  } finally {
    bitmap.close();
  }
}
